<template>
  <div class="rtable">
    <transition name="rtable-fade">
      <div v-if="selectable && selected.size" class="rtable__bulk">
        <span class="rtable__bulk-count">{{ selected.size }} selected</span>

        <slot name="bulk-actions" :selected="[...selected]" :clear="clearSelection">
          <button v-for="ba in bulkActions" :key="ba.key" type="button" class="rtable__bulk-btn"
            :class="{ 'rtable__bulk-btn--danger': ba.danger }"
            @click="$emit('bulk-action', { action: ba.key, ids: [...selected] })">
            {{ ba.label }}
          </button>
        </slot>

        <button type="button" class="rtable__bulk-clear" @click="clearSelection">Clear</button>
      </div>
    </transition>

    <div class="rtable__scroll">
      <table class="rtable__table">
        <thead>
          <tr>
            <th v-if="selectable" class="rtable__th rtable__th--checkbox">
              <label class="rtable__checkbox">
                <input type="checkbox" :checked="allSelected" :indeterminate.prop="someSelected && !allSelected"
                  @change="toggleAll" />
                <span class="rtable__checkbox-box"></span>
              </label>
            </th>

            <!-- Caller-defined header cells -->
            <slot name="header">
              <th v-for="col in columns" :key="col.key" class="rtable__th">{{ col.label }}</th>
            </slot>

            <th v-if="actions" class="rtable__th rtable__th--actions"></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, index) in rows" :key="rowId(row, index)" class="rtable__row"
            :class="{ 'is-selected': selectable && selected.has(rowId(row, index)), 'is-menu-open': openMenuId === rowId(row, index) }">
            <td v-if="selectable" class="rtable__td rtable__td--checkbox">
              <label class="rtable__checkbox">
                <input type="checkbox" :checked="selected.has(rowId(row, index))"
                  @change="toggleRow(rowId(row, index))" />
                <span class="rtable__checkbox-box"></span>
              </label>
            </td>

            <!-- Caller-defined body cells -->
            <slot name="row" :row="row" :selected="selected.has(rowId(row, index))"
              :toggle="() => toggleRow(rowId(row, index))">
              <td v-for="col in columns" :key="col.key" class="rtable__td">{{ row[col.key] }}</td>
            </slot>

            <td v-if="actions" class="rtable__td rtable__td--actions">
              <div class="rtable__menu-wrap">
                <button type="button" class="rtable__menu-btn" :ref="el => setMenuBtnRef(el, rowId(row, index))"
                  aria-label="Row actions" @click.stop="toggleMenu(rowId(row, index), $event)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round">
                    <circle cx="12" cy="5" r="1.4" />
                    <circle cx="12" cy="12" r="1.4" />
                    <circle cx="12" cy="19" r="1.4" />
                  </svg>
                </button>

                <Teleport to="body">
                  <transition name="rtable-menu">
                    <ul v-if="openMenuId === rowId(row, index)" class="rtable__menu rtable__menu--modern"
                      :class="{ 'rtable__menu--upward': menuOpensUpward }" :style="menuStyle" @click.stop>
                      <slot name="menu" :row="row" :close="() => (openMenuId = null)">
                        <template v-for="item in actionItems" :key="item.key">
                          <li v-if="item.dividerBefore" class="rtable__menu-divider" role="separator"></li>
                          <li>
                            <button type="button" class="rtable__menu-item"
                              :class="{ 'rtable__menu-item--danger': item.danger }"
                              @click="handleAction(item.key, row)">
                              <i v-if="item.icon" :class="item.icon"></i>
                              <span>{{ item.label }}</span>
                            </button>
                          </li>
                        </template>
                      </slot>
                    </ul>
                  </transition>
                </Teleport>
              </div>
            </td>
          </tr>

          <tr v-if="!rows.length">
            <td class="rtable__empty" :colspan="totalColspan">
              <slot name="empty">{{ emptyText }}</slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue'
export default {
  name: 'ReusableTable',
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    selectable: {
      type: Boolean,
      default: true
    },
    actions: {
      type: Boolean,
      default: true
    },
    emptyText: {
      type: String,
      default: 'No records found.'
    },
    columns: {
      type: Array,
      default: () => []
    },
    bulkActions: {
      type: Array,
      default: () => [
        { key: 'export', label: 'Export' },
        { key: 'delete', label: 'Delete', danger: true }
      ]
    },
    actionItems: {
      type: Array,
      default: () => [
        { key: 'view', label: 'View' },
        { key: 'edit', label: 'Edit' },
        { key: 'delete', label: 'Delete', danger: true }
      ]
    }
  },

  emits: ['action', 'bulk-action', 'update:selected'],

  data() {
    return {
      selected: new Set(),
      openMenuId: null,
      menuBtnRefs: {},
      menuStyle: {
        position: 'fixed',
        zIndex: 9999,
        minWidth: '220px',
        left: '0px',
        top: '0px',
        bottom: 'auto'
      },
      menuOpensUpward: false,
      warnedMissingKey: false
    }
  },

  computed: {
    allSelected() {
      return this.rows.length > 0 && this.selected.size === this.rows.length
    },
    someSelected() {
      return this.selected.size > 0
    },
    totalColspan() {
      let n = this.columns.length || 1
      if (this.selectable) n += 1
      if (this.actions) n += 1
      return n
    }
  },

  mounted() {
    document.addEventListener('click', this.closeMenuOnOutsideClick)
    document.addEventListener('keydown', this.closeMenuOnEscape)
    window.addEventListener('scroll', this.repositionOnScrollOrResize, true)
    window.addEventListener('resize', this.repositionOnScrollOrResize)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeMenuOnOutsideClick)
    document.removeEventListener('keydown', this.closeMenuOnEscape)
    window.removeEventListener('scroll', this.repositionOnScrollOrResize, true)
    window.removeEventListener('resize', this.repositionOnScrollOrResize)
  },

  methods: {
    rowId(row, index) {
      const id = row?.[this.rowKey]
      if (id === undefined || id === null) {
        if (!this.warnedMissingKey && typeof console !== 'undefined') {
          console.warn(
            `[ReusableTable] row-key "${this.rowKey}" is missing on one or more rows. ` +
            `Falling back to index-based identity — selection/menu state may not persist ` +
            `correctly across re-sorts or filters. Pass a row-key that actually exists on your data.`
          )
          this.warnedMissingKey = true
        }
        return `__idx_${index}`
      }
      return id
    },

    toggleAll() {
      if (this.allSelected) {
        this.selected = new Set()
      } else {
        this.selected = new Set(this.rows.map((r, i) => this.rowId(r, i)))
      }
      this.emitSelection()
    },

    toggleRow(id) {
      const next = new Set(this.selected)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      this.selected = next
      this.emitSelection()
    },

    clearSelection() {
      this.selected = new Set()
      this.emitSelection()
    },

    emitSelection() {
      this.$emit('update:selected', [...this.selected])
    },

    toggleMenu(id, event) {
      if (this.openMenuId === id) {
        this.openMenuId = null
        return
      }
      this.openMenuId = id
      this.$nextTick(() => this.updateMenuPosition(id))
    },

    setMenuBtnRef(el, id) {
      if (el) {
        this.menuBtnRefs[id] = el
      }
    },

    updateMenuPosition(id) {
      const btn = this.menuBtnRefs[id]
      if (!btn) return

      const rect = btn.getBoundingClientRect()
      const menuWidth = 220
      const menuHeightEstimate = 240
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const openUpward = spaceBelow < menuHeightEstimate && spaceAbove > spaceBelow

      this.menuOpensUpward = openUpward

      let left = rect.right - menuWidth
      if (left < 8) left = 8
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8
      }

      this.menuStyle = {
        position: 'fixed',
        zIndex: 9999,
        minWidth: menuWidth + 'px',
        left: left + 'px',
        ...(openUpward
          ? { bottom: (window.innerHeight - rect.top + 8) + 'px', top: 'auto' }
          : { top: (rect.bottom + 8) + 'px', bottom: 'auto' }
        )
      }
    },

    handleAction(action, row) {
      this.openMenuId = null
      this.$emit('action', { action, row })
    },

    closeMenuOnOutsideClick() {
      this.openMenuId = null
    },

    closeMenuOnEscape(e) {
      if (e.key === 'Escape') {
        this.openMenuId = null
      }
    },

    repositionOnScrollOrResize() {
      if (this.openMenuId !== null) {
        this.updateMenuPosition(this.openMenuId)
      }
    },

    closeMenu() {
      this.openMenuId = null
    }
  },

  expose: ['selected', 'clearSelection', 'closeMenu']
}
</script>

<style scoped>
.rtable {
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.rtable * {
  box-sizing: border-box;
}

/* Bulk action bar */
.rtable__bulk {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  margin-bottom: 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.65);
  box-shadow: 0 6px 20px -10px rgba(93, 28, 106, 0.25);
}

.rtable__bulk-count {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
  margin-right: 4px;
}

.rtable__bulk-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 9px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-dark);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.rtable__bulk-btn:hover {
  background: var(--color-tint-light);
}

.rtable__bulk-btn--danger {
  color: #b3261e;
  border-color: rgba(179, 38, 30, 0.25);
}

.rtable__bulk-btn--danger:hover {
  background: rgba(179, 38, 30, 0.08);
}

.rtable__bulk-clear {
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.rtable__bulk-clear:hover {
  color: var(--color-text-dark);
}

.rtable__scroll {
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.65);
  box-shadow: 0 10px 34px -14px rgba(93, 28, 106, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

@media (max-width: 900px) {
  .rtable__scroll {
    overflow-x: auto;
  }
}
.rtable__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

/* Head */
.rtable__th {
  text-align: left;
  padding: 14px 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
}

.rtable__th--checkbox,
.rtable__th--actions {
  width: 44px;
}

/* Rows — the "pop up" hover effect */
.rtable__row {
  position: relative;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.rtable__row:hover,
.rtable__row.is-menu-open {
  transform: translateY(-3px) scale(1.004);
  background: rgba(255, 255, 255, 0.75);
  box-shadow: 0 -8px 25px -8px rgba(93, 28, 106, 0.15), 0 8px 25px -8px rgba(93, 28, 106, 0.15), 0 4px 12px -6px rgba(93, 28, 106, 0.08);
  z-index: 2;
  position: relative;
  border-radius: 8px;
}

.rtable__row.is-selected {
  background: rgba(255, 241, 211, 0.55);
  /* --color-tint-light, translucent */
}

.rtable__td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
  font-size: 13.5px;
  color: var(--color-text-dark);
  vertical-align: middle;
}

.rtable__row:last-child .rtable__td {
  border-bottom: none;
}

.rtable__empty {
  padding: 40px 16px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 13.5px;
}

/* Checkbox */
.rtable__checkbox {
  display: inline-flex;
  cursor: pointer;
  position: relative;
}

.rtable__checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.rtable__checkbox-box {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1.5px solid var(--color-border);
  background: var(--color-surface);
  display: grid;
  place-items: center;
  transition: background 0.16s ease, border-color 0.16s ease;
}

.rtable__checkbox-box::after {
  content: '';
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.16s ease;
  margin-bottom: 2px;
}

.rtable__checkbox input:checked+.rtable__checkbox-box {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  border-color: transparent;
}

.rtable__checkbox input:checked+.rtable__checkbox-box::after {
  transform: rotate(45deg) scale(1);
}

.rtable__checkbox input:indeterminate+.rtable__checkbox-box {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  border-color: transparent;
}

.rtable__checkbox input:indeterminate+.rtable__checkbox-box::after {
  content: '';
  width: 9px;
  height: 2px;
  border: none;
  background: #fff;
  transform: scale(1) rotate(0);
  margin-bottom: 0;
}

/* Actions / dropdown menu */
.rtable__td--actions {
  text-align: right;
}

.rtable__menu-wrap {
  position: relative;
  display: inline-block;
}

.rtable__menu-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.rtable__menu-btn svg {
  width: 17px;
  height: 17px;
}

.rtable__menu-btn:hover {
  background: var(--color-tint-light);
  color: var(--color-text-dark);
}

.is-menu-open .rtable__menu-btn {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
}

.rtable__menu {
  min-width: 200px;
  padding: 7px;
  margin: 0;
  list-style: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px) saturate(200%);
  -webkit-backdrop-filter: blur(20px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow:
    0 20px 40px -14px rgba(93, 28, 106, 0.35),
    0 4px 12px -4px rgba(93, 28, 106, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transform-origin: top right;
}
:slotted(.rtable__menu li) {
  list-style: none;
}

:slotted(.dropdown-item) {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  cursor: pointer;
  text-decoration: none;
  transition: background 0.16s ease, padding-left 0.16s ease;
}

:slotted(.dropdown-item):hover {
  background: var(--color-tint-light);
  padding-left: 13px;
}

:slotted(.dropdown-item) i {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  font-size: 12px;
  border-radius: 7px;
  background: var(--color-tint-light);
}

:slotted(.dropdown-divider) {
  height: 1px;
  margin: 6px 6px;
  border: none;
  background: linear-gradient(90deg, transparent, var(--color-border) 20%, var(--color-border) 80%, transparent);
}

/* small caret pointing back at the trigger button, for a "connected" feel */
.rtable__menu::before {
  content: '';
  position: absolute;
  top: -5px;
  right: 12px;
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.92);
  border-left: 1px solid rgba(255, 255, 255, 0.8);
  border-top: 1px solid rgba(255, 255, 255, 0.8);
  transform: rotate(45deg);
  border-radius: 2px 0 0 0;
}

.rtable__menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  cursor: pointer;
  text-align: left;
  transition: background 0.16s ease, transform 0.16s ease, padding-left 0.16s ease;
}

.rtable__menu-item:hover {
  background: var(--color-tint-light);
  padding-left: 13px;
}

.rtable__menu-item:active {
  transform: scale(0.98);
}

.rtable__menu-item svg,
.rtable__menu-item i {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  font-size: 12px;
  border-radius: 7px;
  background: var(--color-tint-light);
  color: var(--color-text-muted);
  transition: background 0.16s ease, color 0.16s ease;
}

.rtable__menu-item:hover svg,
.rtable__menu-item:hover i {
  background: rgba(255, 255, 255, 0.9);
}

.rtable__menu-item--danger {
  color: #b3261e;
}

.rtable__menu-item--danger svg,
.rtable__menu-item--danger i {
  color: #b3261e;
  background: rgba(179, 38, 30, 0.1);
}

.rtable__menu-item--danger:hover {
  background: rgba(179, 38, 30, 0.08);
}

.rtable__menu-item--danger:hover svg,
.rtable__menu-item--danger:hover i {
  background: rgba(179, 38, 30, 0.16);
}

.rtable__menu-divider {
  height: 1px;
  margin: 6px 6px;
  background: linear-gradient(90deg, transparent, var(--color-border) 20%, var(--color-border) 80%, transparent);
}

/* Transitions */
.rtable-fade-enter-active,
.rtable-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.rtable-fade-enter-from,
.rtable-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.rtable-menu-enter-active {
  transition: opacity 0.16s ease, transform 0.16s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.rtable-menu-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.rtable-menu-enter-from,
.rtable-menu-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.96);
}

.rtable__menu {
  transform-origin: top right;
}
</style>